import './InputField.scss';

import { LockSimple } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useField } from 'formik';
import { dash } from 'radash';
import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  ReactNode,
  useId,
} from 'react';

import { useSuperFormik } from '../../SuperFormik';
import { Description } from '../Description';
import { ErrorMessage } from '../ErrorMessage';
import { Input } from '../Input';
import { Label } from '../Label';

export type InputFieldProps = Pick<
  ComponentPropsWithoutRef<'input'>,
  | 'type'
  | 'required'
  | 'disabled'
  | 'placeholder'
  | 'autoComplete'
  | 'autoFocus'
  | 'accept'
  | 'multiple'
  | 'onChange'
> & {
  name: string;
  label?: ReactNode;
  description?: ReactNode;
  showErrors?: boolean;
  prefix?: ReactNode;
};

const className = 'c-InputField';

export function InputField({
  description,
  type = 'text',
  multiple,
  showErrors = true,
  onChange,
  prefix,
  ...props
}: InputFieldProps) {
  const { name, disabled } = props;

  const id = useId();
  const [, meta, helpers] = useField(name);
  const { required, label, testIdPrefix } = useSuperFormik(props);
  const { value, error, touched } = meta;
  const { setValue, setTouched } = helpers;

  const hasError = Boolean(error) && touched;
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange?.(event);
    if (type === 'file') {
      setValue(
        multiple
          ? Array.from(event.currentTarget.files ?? [])
          : event.currentTarget.files?.[0],
      );
    } else {
      setValue(event.currentTarget.value);
    }
  };

  const onBlur = () => {
    setTouched(true);
  };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      <div className={`${className}__input_wrapper`}>
        {prefix && <div className={`${className}__prefix`}>{prefix}</div>}
        <Input
          id={id}
          type={type}
          required={required}
          multiple={multiple}
          hasError={hasError}
          aria-describedby={classNames({
            [descriptionId]: description,
            [errorId]: hasError && showErrors,
          })}
          data-testid={dash(`${testIdPrefix}${name}-input`)}
          value={type === 'file' ? undefined : value ?? ''}
          onBlur={onBlur}
          onChange={handleChange}
          {...props}
        />
        {disabled && (
          <LockSimple className={`${className}__lock_icon`} weight={'bold'} />
        )}
      </div>
      {description && !error && (
        <Description id={descriptionId}>{description}</Description>
      )}
      {hasError && showErrors && (
        <ErrorMessage id={errorId}>{error}</ErrorMessage>
      )}
    </div>
  );
}
